import type { GetServerSideProps } from 'next'
import type { SSRConfig } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { match } from 'ts-pattern'

import { SupportedMarketKey, supportedMarketSchema } from '@nxt-insurance/constants'
import Spinner from '@nxt-insurance/ui-kit/components/Spinner'

import { runQuery } from '@/cms/client'
import type { Navbar, RouteWithPage } from '@/cms/pages'
import { allRoutesWithSlug, redirectQuery, routeQuery, siteSettingsQueryNavbar } from '@/cms/pages'
import { Page } from '@/components/Page'
import { PageWithPreview } from '@/components/Page/PageWithPreview'
import { PageMetaDescription } from '@/components/PageMetaDescription'
import Header from '@/components/sections/Header'
import { APP_ENV, DOMAIN } from '@/config/env'
import { EVENTS } from '@/constants/analytics'
import { useAlternateLinks } from '@/hooks/useAlternateLinks'
import { useAppendCurrentQueryParametersToAnchorTags } from '@/hooks/useAppendQueryParametersToAnchorTags'
import PreviewProvider from '@/pages/PreviewProvider'
import { sendPlausibleEvent } from '@/server/analytics/plausible'
import { trackEventServerSide } from '@/server/analytics/trackEventServerSide'
import * as errorReportingService from '@/services/errorReporting'
import { getSplitClient, getSplitUserKey } from '@/services/splitio'
import {
  getFacebookParams,
  getFromCookiesOrUrlParams,
  readUrlParams,
  setFacebookCookies,
} from '@/utils/cookies'
import { generateFaqsSchema } from '@/utils/generateFaqsSchema'
import { getLocaleParts } from '@/utils/locale'
import { getNavbarFromSplit, getPageFromSplit, getPageWithSectionsFromSplit } from '@/utils/page'
import { getPageName, getPathWithParams, normalizePathForTracking } from '@/utils/url'

type Props = SSRConfig & {
  preview: boolean
  data: {
    // There is actually no problem with this type but Zod has sometimes problems
    // with very deep types
    // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error
    // @ts-ignore-next-line
    route: RouteWithPage
    navbar?: Navbar
    allAlternativeRoutes: Record<'market', string>[]
    faqsSchema: ReturnType<typeof generateFaqsSchema>
  }
}

const DynamicNotFoundSection = dynamic(
  async () => import('@/components/sections/NotFoundSection'),
  {
    loading: () => <Spinner />,
    ssr: false,
  }
)
const Footer = dynamic(async () => import('@/components/sections/Footer').then((mod) => mod.Footer))

const CmsPage = (props: Props) => {
  const { locale, asPath, locales } = useRouter()
  const [lang, market] = getLocaleParts(locale)
  const url = useMemo(() => new URL(asPath, `https://${DOMAIN}`), [asPath])
  const pageName = getPageName(url.pathname)

  const [folder, slug] = pageName.split('/')
  const { route, navbar, allAlternativeRoutes = [] } = props.data

  const slugs = useMemo(() => {
    if (!route) {
      return {}
    }

    const convertedSlugs: Record<string, { current: string } | undefined> = {}
    if (allAlternativeRoutes.length > 1) {
      allAlternativeRoutes.forEach((r) => {
        locales
          ?.filter((l) => l.endsWith(`-${r.market}`))
          .forEach((l) => {
            const [currentLang] = getLocaleParts(l)
            convertedSlugs[currentLang] = {
              current: slug ?? folder ?? '',
            }
          })
      })
      return convertedSlugs
    }
    Object.entries(route.slugs).forEach(([l, s]) => {
      if (!s) {
        convertedSlugs[l] = s
      }
      const current = s?.current
      if (current === 'index') {
        convertedSlugs[l] = {
          current: '',
        }
        return
      }
      if (!folder || (folder && !slug)) {
        convertedSlugs[l] = { current: current ?? '' }
        return
      }
      convertedSlugs[l] = { current: `${route.folders?.[l]}/${current}` }
    })
    return convertedSlugs
  }, [route, allAlternativeRoutes, locales, slug, folder])

  const alternateLinks = useAlternateLinks(
    slugs,
    props.data.allAlternativeRoutes?.map((r) => {
      const parsed = supportedMarketSchema.safeParse(r.market)

      if (parsed.success) {
        return parsed.data
      }

      return SupportedMarketKey.De
    })
  )

  useAppendCurrentQueryParametersToAnchorTags()

  if (!route) {
    return (
      <>
        <PageMetaDescription />
        <Header pageType="not_found" navbar={navbar} alternateLinks={alternateLinks} />
        <DynamicNotFoundSection />
        <Footer />
      </>
    )
  }

  if (!props.preview) {
    return (
      <>
        {props.data.faqsSchema ? (
          <Head>
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(props.data.faqsSchema) }}
            />
          </Head>
        ) : null}
        <Page route={route} navbar={navbar} alternateLinks={alternateLinks} />
      </>
    )
  }

  return (
    <PreviewProvider>
      <PageWithPreview
        navbar={navbar}
        alternateLinks={alternateLinks}
        slug={slug || folder || 'index'}
        folder={slug === undefined ? undefined : folder}
        lang={lang}
        market={market}
      />
    </PreviewProvider>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async (ctx) => {
  // This value is considered fresh for 5 min (s-maxage=300).
  // If a request is repeated within the next 5 min, the previously
  // cached value will still be fresh. If the request is repeated before 359 seconds,
  // the cached value will be stale but still render (stale-while-revalidate=359).
  //
  // In the background, a revalidation request will be made to populate the cache
  // with a fresh value. If you refresh the page, you will see the new value.
  if (APP_ENV !== 'development') {
    ctx.res.setHeader('Cache-Control', 'public, s-maxage=300, stale-while-revalidate=359')
  }
  const slugFromContext = ctx.params?.slug ?? ['']
  const pathname = Array.isArray(slugFromContext) ? slugFromContext.join('/') : slugFromContext
  const locale =
    ctx.locale === 'default' || ctx.locale === undefined
      ? (ctx.defaultLocale as string)
      : ctx.locale
  if (locale === 'default') {
    return {
      notFound: true,
    }
  }
  if (!locale) {
    throw new Error('Assertion: missing locale')
  }
  const requestUrl = new URL(ctx.resolvedUrl, `https://${DOMAIN}`)
  const query = requestUrl.searchParams

  // referrerName and referralCode are necessary cookies
  // campaign cookie is only used for getting data from sanity
  const { referrerName, referralCode, utm_source, utm_medium, utm_campaign, fbclid } =
    getFromCookiesOrUrlParams(query)
  const splitClient = getSplitClient()
  await splitClient.ready()

  const pageName = pathname
  if (
    pageName.toLowerCase().replaceAll('/', '') === 'referral' &&
    (!referrerName || !referralCode)
  ) {
    errorReportingService.reportErrorMessage('Mandatory parameter "r" | "n" for referral is empty')
  }

  const [lang, market] = getLocaleParts(locale)

  let [folder, slug] = pageName.split('/')

  if (pageName.split('/').length > 2) {
    errorReportingService.reportWarning(`Unsupported query content: ${JSON.stringify(pageName)}`)
    slug = ''
    folder = ''
  }

  const path = `/${lang}-${market}/${pathname}`
  const [route, siteSettingsNavbar, redirect, allAlternativeRoutes] = await Promise.all([
    runQuery(routeQuery, {
      lang,
      market,
      slug: slug || folder || 'index',
      folder: (slug === undefined ? undefined : folder) ?? null,
    }),
    runQuery(siteSettingsQueryNavbar, { lang, market }),
    runQuery(redirectQuery, { path }),
    runQuery(allRoutesWithSlug(), { market, slug: slug || folder }),
  ])

  if (redirect) {
    return {
      redirect: {
        destination: getPathWithParams(redirect.newPath, { params: query.toString() }),
        permanent: false,
      },
    }
  }

  if (!route || (folder && slug && !route.folder)) {
    return {
      notFound: true,
    }
  }

  if (route.redirectActive) {
    return {
      redirect: {
        destination: getPathWithParams(route.redirectTarget ?? '/', { params: query.toString() }),
        permanent: false,
      },
    }
  }

  if (APP_ENV === 'production' && !route.availableEnvironments?.includes('production')) {
    return {
      notFound: true,
    }
  }

  if (!route.page || !siteSettingsNavbar) {
    return {
      notFound: true,
    }
  }

  const [page, navbar] = await Promise.all([
    getPageFromSplit(route, lang, ctx.req, ctx.res),
    getNavbarFromSplit(siteSettingsNavbar.navbar, lang, { req: ctx.req, res: ctx.res }),
  ])

  if (!page) {
    return {
      notFound: true,
    }
  }
  const pageWithSectionsFromSplit = await getPageWithSectionsFromSplit(
    page,
    lang,
    market,
    ctx.req,
    ctx.res
  )
  const userKey = getSplitUserKey({
    req: ctx.req,
    res: ctx.res,
  })
  setFacebookCookies(readUrlParams(query), {
    req: ctx.req,
    res: ctx.res,
  })
  const fbParams = getFacebookParams({
    req: ctx.req,
    res: ctx.res,
  })
  const { pageType } = route
  const productViewedProperties = match(pageType)
    .returnType<Record<string, unknown> | undefined>()
    .with(
      {
        _type: 'website.pageType.productPage',
      },
      ({ abstractProduct }) => {
        const productId = abstractProduct.defaultProduct.productKey.productKey
        const abstractProductId = abstractProduct.abstractProductKey
        const flowName = abstractProduct.webBuyingFlow
        return {
          product_id: productId,
          abstract_product: abstractProductId,
          utm_source,
          utm_medium,
          utm_campaign,
          flowName,
          fbclid,
          ...fbParams,
        }
      }
    )
    .with(
      {
        _type: 'website.pageType.bundleProductPage',
      },
      ({ bundleName, flowName }) => {
        const productId = bundleName
        return {
          product_id: productId,
          utm_source,
          utm_medium,
          utm_campaign,
          flowName,
          fbclid,
          ...fbParams,
        }
      }
    )
    .with(
      {
        _type: 'website.pageType.mgaLightProductPage',
      },
      ({ mgaLightProduct }) => {
        const productId = mgaLightProduct.mgaLightProductKey
        return {
          product_id: productId,
          utm_source,
          utm_medium,
          utm_campaign,
          fbclid,
          ...fbParams,
        }
      }
    )
    .otherwise(() => undefined)
  if (
    productViewedProperties &&
    // adding both ways of writing the header since sometimes it is lower case
    ctx.req.headers['X-Purpose'] !== 'preview' &&
    ctx.req.headers['x-purpose'] !== 'preview'
  ) {
    const { normalizedPath } = normalizePathForTracking(ctx.resolvedUrl, locale)

    const trackedProperties = {
      ...productViewedProperties,
      path: normalizedPath,
    }

    void trackEventServerSide({
      serverContext: { req: ctx.req, res: ctx.res },
      anonymousId: userKey,
      event: EVENTS.ProductViewed,
      properties: trackedProperties,
      context: { page: { externalReferrer: 'no-external-referrer' } },
    })
      .then((payload) =>
        sendPlausibleEvent({
          req: ctx.req,
          event: payload.event,
          properties: payload.properties,
        })
      )
      .catch((trackingError: unknown) =>
        errorReportingService.reportError(trackingError, {
          context: 'ProductViewed tracking failed',
        })
      )
  }
  if (pageType?._type === 'website.pageType.productPage') {
    const faqsSchema = generateFaqsSchema(pageType.abstractProduct.defaultProduct.faqs ?? [])

    return {
      props: {
        ...(await serverSideTranslations(locale, ['common'])),
        preview: ctx.preview ?? false,
        data: {
          allAlternativeRoutes,
          navbar,
          route: {
            ...route,
            page: pageWithSectionsFromSplit,
          },
          faqsSchema,
        },
      },
    }
  }
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      preview: ctx.preview ?? false,
      data: {
        allAlternativeRoutes,
        navbar,
        route: {
          ...route,
          page: pageWithSectionsFromSplit,
        },
        faqsSchema: null,
      },
    },
  }
}

export default CmsPage
